import {toArray, first, isEqual} from 'lodash';
import type {Map} from 'immutable';
import {prepend} from '@packages/utilities/array';
import {
  NORMAL_NODE,
  TEXT_NODE,
  getNativeId,
  getWindow,
  getDocument,
} from '@packages/shared/utilities/DomUtils';
import {WEBFLOW_COMPONENT_ROOT_ATTR} from '@packages/systems/core/constants/SharedConfig';
import {
  type NativeId,
  getGuidNullable,
} from '@packages/systems/core/utils/NativeIdUtils';
import {type Box} from '@packages/utilities/box';
import {isSpaceOrNbsp} from '@packages/systems/core/utils/TextUtils';
import {
  A,
  B,
  I,
  K,
  C,
  X,
  S,
  U,
  Z,
  V,
  ENTER,
  SPACE,
  ESC,
  TAB,
  ASTERISK,
  UNDERSCORE,
  OPENING_PARENTHESES,
  CLOSING_PARENTHESES,
  BACKTICK,
  UP,
  DOWN,
  RIGHT,
  isArrow,
} from '@packages/utilities/key-utils';
import {SHORTCUTS} from '@packages/shared/constants/ContentEditor';

import type {
  ParsedSelectionType,
  RangeSelectionWithNodesType,
  InlineMarkdownType,
} from '@packages/shared/types/ContentEditor';
import {
  type SelectionRecordType,
  RangeSelectionRecord,
  type ActiveUploadRecordType,
} from '@packages/shared/records';
import type {ContentEditorActive} from '@packages/shared/states';
import {
  selectionUpdated,
  textOfNodeSelectedInInstanceEditingEnded,
  type ImgNodeRectsType,
} from './ContentEditorActionCreators';

import {
  type Expression,
  type ExpressionElement,
  textTypeName,
  EElement,
  TNumber,
  ENumber,
  TText,
  EText,
} from '@packages/systems/wfdl-ast';
import {dataTypeChanged} from '@packages/systems/element-action-creators';
import {generateGuid} from '@packages/utilities/guid';
import {Dispatch} from '@packages/systems/flux';
import {
  type Type,
  type EnvironmentContextType,
  Plugins,
  Component,
  Expressions,
  Types,
  getMeta,
} from '@packages/systems/wfdl';
import {
  getEditableTypographicElement,
  getEditableRTEElement,
} from '@packages/shared/render/utils/ContentEditorUtils';
import {
  getInnermostSymbolInstance,
  getTargetElementOrInstance,
  getChildren,
  getClosestFigureElement,
  getClosestListElement,
  getComponentInstRootElement,
  getRelativePath,
  getCurrentComponent,
  isUserComponent,
} from '@packages/utilities/platform';
import {
  isRichText,
  isStringElement,
  isParagraph,
  isEmbed,
} from '@packages/utilities/elementTypeUtils';
import {
  getOutermostFieldIdFromPath,
  getLinkedFieldsPathMapForElement,
} from '@packages/utilities/data-binding-paths';
import {namesEqual} from '@packages/systems/wfdl-Qualified';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {Tree} from '@packages/systems/tree';
import {
  cleanSubtreeForSave,
  isHiddenFromContentEditable,
} from './ContentEditorStoreUtils';
import {
  assertNodeSelectionRecordType,
  assertRangeSelectionRecordType,
  assertRangeSelectionType,
  getCharAfterCaret,
  getSelectedElements,
  getSelectedFigureSafariFallback,
  isCodeBlockSelected,
  isEmbedSelected,
  isFigureSelected,
  isUserComponentSelected,
  getAllTextInElement,
  getClosestTopLevelTypoElement,
  isNumberEditing,
  getTargetElement,
  getClosestTopLevelElement,
  hasCaretMoved,
  reifySelectionRecord,
} from '@packages/systems/ContentEditor';
import {getInlineMarkdownTypeInText} from './ContentEditorUtils';
import {
  NumberConstraintsType,
  parseNumberWithConstraints,
} from '@packages/systems/core/utils/NumberUtils';
import {type ContentEditorViewportAdapter} from '@packages/systems/ContentEditorViewportAdapter';
import {
  normalizeRichTextNodes,
  removeEmptyDataObjects,
} from '@packages/systems/cms/utils';

// TODO (Yoav): These all need tests

// Note: React wraps text_nodes in <comments> to keep track of them. These comment nodes show up in
// nativeNode.childNodes so keep that in mind.
// we want to filter out the comment nodes that react includes and potentially other types when traversing the native tree
export const validNodeFilter = (nativeNode: Node): boolean => {
  const nodeType = nativeNode.nodeType;
  return nodeType === NORMAL_NODE || nodeType === TEXT_NODE;
};

const getChildrenIndexPathToElement = (
  root: ExpressionElement,
  targetId: string
): null | Array<number> => {
  if (root.id === targetId) return [];
  const children = getChildren(root);
  if (!children) return null;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child && Expressions.allElements(EElement(child))[targetId]) {
      const res = getChildrenIndexPathToElement(child, targetId);
      if (res) return prepend(i, res);
    }
  }
  return null;
};

export const getNativeNodeFromElement = (
  subtree: ExpressionElement,
  elementId: string,
  nativeSubtree: HTMLElement
): HTMLElement | undefined => {
  const path = getChildrenIndexPathToElement(subtree, elementId);

  if (!path) return undefined;
  if (!path.length && subtree.id !== elementId) return undefined;

  // TODO (Yoav): add memoization
  return path.reduce<undefined | HTMLElement>(
    (finder, index) =>
      finder
        ? (
            toArray(finder.childNodes).filter(
              validNodeFilter
            ) as Array<HTMLElement>
          )[index]
        : finder,
    nativeSubtree
  );
};

export const isNativeNodeFixedPosition = (nativeEl: HTMLElement): boolean => {
  const win = getWindow(nativeEl);
  let node: Node = nativeEl;

  while (node && node.nodeType === NORMAL_NODE) {
    const element = node as Element;

    if (win && win.getComputedStyle(element).position === 'fixed') {
      return true;
    }

    if (!element.parentNode) {
      return false;
    }

    node = element.parentNode;
  }

  return false;
};

const isCodeBlockElement = (element: Element | null): boolean => {
  if (!element) return false;

  return (
    element.nodeName === 'PRE' && element.classList.contains('w-code-block')
  );
};

const isRichTextElement = (element: Element | null): boolean => {
  if (!element) return false;

  return element.nodeName === 'DIV' && element.classList.contains('w-richtext');
};

/**
 * Traverse up the DOM to find the nearest code block element
 */
const getCodeBlockElement = (element: Element | null): Element | null => {
  let e = element;

  while (e) {
    if (isCodeBlockElement(e)) return e;

    if (isRichTextElement(e)) break;

    e = e.parentElement;
  }

  return null;
};

type ClickEventData = Pick<MouseEvent, 'clientX' | 'clientY' | 'target'>;

export const getRangeFromClickEvent = (event: ClickEventData) => {
  const target = event.target;
  // @ts-expect-error - TS18047 - 'target' is possibly 'null'. | TS2339 - Property 'ownerDocument' does not exist on type 'EventTarget'.
  const doc = target.ownerDocument;
  const x = event.clientX;
  const y = event.clientY;

  let range;

  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(x, y);
    range = doc.createRange();
    range.setStart(pos.offsetNode, pos.offset);
    range.collapse(true);
  } else if (doc.caretRangeFromPoint) {
    // Webkit
    range = doc.caretRangeFromPoint(x, y);
    if (!range) {
      // Safari will only return a range from caretRangeFromPoint if the
      // element is contenteditable = true. elementFromPoint() will return the
      // type 1 element. eg: when double clicking on a rich text element,
      // it'll return the paragraph you clicked on but not the textNode. That
      // should be enough information to get a meaningful selection to init
      // the contentEditor
      const rangeEl = doc.elementFromPoint(x, y);
      rangeEl.setAttribute('contenteditable', 'true');
      range = doc.caretRangeFromPoint(x, y);

      // Safari hack for the Data Manager
      if (!range) {
        range = doc.createRange();
        range.setStart(rangeEl, 0);
      }
      rangeEl.removeAttribute('contenteditable');
    }
  }

  return range;
};

export const getClientRectFromLinkNode = (
  nativeSubtree: HTMLElement,
  adapter: ContentEditorViewportAdapter
): ParsedSelectionType | null | undefined => {
  const nativeRect = nativeSubtree.getBoundingClientRect();
  const nodeClientRect = adapter.transformClientRect(nativeRect, {
    nativeNode: nativeSubtree,
  });

  return {
    type: 'Node',
    nodeClientRect,
  };
};

/**
 * For elements with `display: contents` (e.g. code-island custom elements),
 * getBoundingClientRect returns a zero rect and offsetTop/offsetLeft are 0.
 * Walk into children (including open shadow DOM) to find the first descendant
 * that has actual layout dimensions, so we can use its rect and offsets for
 * toolbar positioning and content-editor outlines.
 *
 * Fast path: code-island shadow roots use `[data-root="true"]` (often
 * display:contents); prefer its element children before scanning all shadow
 * children (stylesheets, etc.).
 */
export const getVisualElement = (element: Element): Element => {
  const rect = element.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) return element;

  if (element.shadowRoot) {
    const dataRoot = element.shadowRoot.querySelector('[data-root="true"]');
    if (dataRoot) {
      for (const child of Array.from(dataRoot.children)) {
        const found = getVisualElement(child);
        const fr = found.getBoundingClientRect();
        if (fr.width > 0 || fr.height > 0) {
          return found;
        }
      }
      return element;
    }
  }

  const children = element.shadowRoot
    ? Array.from(element.shadowRoot.children)
    : Array.from(element.children);

  for (const child of children) {
    const found = getVisualElement(child);
    const fr = found.getBoundingClientRect();
    if (found !== child || fr.width > 0 || fr.height > 0) {
      return found;
    }
  }

  return element;
};

/**
 * User components (e.g. code-island) may use `display: contents`, so the host
 * has a zero client rect. Use the first in-layout descendant for bounds.
 * Shared by click-init and native-selection sync in {@link parseNativeSelection}.
 */
export const getNativeNodeForToolbarBounds = (
  subtree: ExpressionElement,
  nodeId: string,
  nativeNode: HTMLElement | null | undefined
): HTMLElement | null => {
  if (nativeNode == null) return null;
  const el = Expressions.getElementById(EElement(subtree), nodeId);
  if (el && isUserComponent(el.type)) {
    return getVisualElement(nativeNode) as HTMLElement;
  }
  return nativeNode;
};

export const parseSelectionFromClickEvent = (
  subtree: ExpressionElement,
  initEvent: ClickEventData,
  nativeSubtree: HTMLElement,
  adapter: ContentEditorViewportAdapter
): ParsedSelectionType | null | undefined => {
  if (initEvent) {
    const target = initEvent.target as Element;
    if (
      target?.closest?.('code-island') ||
      target?.hasAttribute?.(WEBFLOW_COMPONENT_ROOT_ATTR)
    ) {
      const componentRoot = findFurthestComponentRoot(target);
      if (componentRoot) {
        const targetElement = getElementForNativeNode(
          subtree,
          componentRoot as Node
        );
        if (targetElement && isUserComponent(targetElement.type)) {
          return parseNativeSelection(
            subtree,
            nativeSubtree,
            {nodeId: targetElement.id},
            adapter
          );
        }
      }
    }

    const clickRange = getRangeFromClickEvent(initEvent);

    // In Safari when double clicking on an image to start editing a rich text
    // field, the rangeFromClickEvent may still be null.
    if (clickRange == null) {
      const targetElement = getElementForNativeNode(
        subtree,
        initEvent.target as Node
      );
      if (!targetElement) return;

      const targetFigure = getClosestFigureElement(
        EElement(subtree),
        targetElement.id
      );
      const nodeId = targetFigure?.id;
      return parseNativeSelection(subtree, nativeSubtree, {nodeId}, adapter);
    }
    return parseNativeSelection(
      subtree,
      nativeSubtree,
      {range: clickRange},
      adapter
    );
  } else {
    // TODO (Yoav): When we integrate with the data manager there won't be a click
    // event for cases when we tab to the rich text field, set the position to the
    // top-left node
  }
};

// @ts-expect-error - TS7006 - Parameter 'n' implicitly has an 'any' type.
const isNativeTextNode = (n) => n.nodeType === TEXT_NODE;

export const getElementForNativeNode = (
  subtree: ExpressionElement,
  nativeNode: Node
) => {
  const nodeId = getNodeIdFromNativeNode({subtree, nativeNode});
  return nodeId ? Expressions.getElementById(EElement(subtree), nodeId) : null;
};

/**
 * Traverse up the DOM to find the furthest element with data-w-root-component
 * stopping when we hit the rich text boundary
 * @param element - The DOM element to start searching from
 * @returns The furthest element with data-w-root-component, or null if not found
 */
export const findFurthestComponentRoot = (
  element: Node | null
): Element | null => {
  // Text nodes (nodeType 3) don't have hasAttribute — start from parentElement.
  let e: Element | null =
    element && element.nodeType === NORMAL_NODE
      ? (element as Element)
      : (element?.parentElement ?? null);
  let furthestComponentRoot: Element | null = null;

  while (e) {
    // No need to traverse further if we hit an active rich text
    if ((e as HTMLElement).contentEditable === 'true') break;

    if (e.hasAttribute(WEBFLOW_COMPONENT_ROOT_ATTR)) {
      furthestComponentRoot = e;
    }

    e = e.parentElement;
  }

  return furthestComponentRoot;
};

/**
 * Get the provided native node's native id.
 * Use this when you need to get the node's full id including the instance suffix (used in Collection Lists).
 * @param subtree - The subtree to search in
 * @param nativeNode - The native node to get the id from
 * @returns The full native id including the instance suffix, or null if not found.
 */
export const getNativeIdFromNativeNode = ({
  subtree,
  nativeNode,
}: {
  subtree: ExpressionElement;
  nativeNode: Node;
}) => {
  return getIdFromNativeNode({
    subtree,
    nativeNode,
    getId: getNativeId,
    returnNativeId: true,
  });
};

/**
 * Get the provided native node's id.
 * Use this when you need to get the node's guid (used in the Expression tree).
 * @param subtree - The subtree to search in
 * @param nativeNode - The native node to get the id from
 * @returns The node guid, or null if not found.
 */
export const getNodeIdFromNativeNode = ({
  subtree,
  nativeNode,
}: {
  subtree: ExpressionElement;
  nativeNode: Node;
}) => {
  return getIdFromNativeNode({
    subtree,
    nativeNode,
    getId: (node) => getGuidNullable(getNativeId(node)),
    returnNativeId: false,
  });
};

/**
 * Used internally by getNativeIdFromNativeNode and getNodeIdFromNativeNode to get the id from a native node.
 * Use those functions instead of this one directly.
 * @param subtree - The subtree to search in
 * @param nativeNode - The native node to get the id from
 * @param getId - The function to get the id from the node. Either returns the guid or the native id.
 * @param returnNativeId - Whether to return the native id or not. Needed for text nodes because the id lookup
 * is different than other node types.
 * @returns The id in the requested format, or null if not found.
 */
const getIdFromNativeNode = ({
  subtree,
  nativeNode,
  getId,
  returnNativeId,
}: {
  subtree: ExpressionElement;
  nativeNode: Node;
  getId: (node: Node) => string | null;
  returnNativeId: boolean;
}): string | null => {
  const nodeType = nativeNode.nodeType;

  // handles an edge case where code block is empty and the click area is the <code> tag
  if (nodeType === NORMAL_NODE && nativeNode.nodeName === 'CODE') {
    const element = getCodeBlockElement(nativeNode.parentElement);

    if (element) {
      return getId(element);
    }
  }

  if (nodeType === NORMAL_NODE) {
    const componentRoot = findFurthestComponentRoot(nativeNode as Element);
    if (componentRoot) {
      return getId(componentRoot);
    }

    return getId(nativeNode);
  } else if (isNativeTextNode(nativeNode)) {
    const componentRoot = findFurthestComponentRoot(nativeNode.parentElement);
    if (componentRoot) {
      return getId(componentRoot);
    }

    const nativeTextNodeWithSiblings = toArray(
      // @ts-expect-error: parentNode is nullable
      nativeNode.parentNode.childNodes
    ).filter(isNativeTextNode);
    // @ts-expect-error - TS2345 - Argument of type 'Node' is not assignable to parameter of type 'ChildNode'.
    const nodeIndex = nativeTextNodeWithSiblings.indexOf(nativeNode);
    const nativeParent = nativeNode.parentNode;
    const nativeParentNativeId = getNativeId(nativeParent);
    const parentNodeId = getGuidNullable(nativeParentNativeId);
    const parent = parentNodeId
      ? Expressions.getElementById(EElement(subtree), parentNodeId)
      : null;

    // There are some cases in the data-manager where the window.selection is
    // inside a different rich text field to the element subtree being
    // passed in where we want to ignore it and can just return null. (That's
    // the known case where it wouldn't be able to getElementById for the parent)
    if (parent) {
      // Embeds are rendered as plain text with their content hidden. If the caret
      // is inside an embed, we want to select the parent node itself.
      if (isEmbed(parent) && nativeParent) {
        return getId(nativeParent);
      }

      const siblings = (getChildren(parent) || []).filter(isStringElement);
      const el = siblings[nodeIndex];
      if (!el) return null;

      // To return the native id, we need to return the parent's id, and not the text node.
      if (returnNativeId && nativeParentNativeId) {
        return nativeParentNativeId;
      }

      return el.id;
    } else {
      // in this case - the nativeNode may be nested and there is no direct parent node
      // e.g. code blocks are structured like <pre><code><span>...</span></code></pre>
      // and the user may have clicked the span element

      const element = getCodeBlockElement(nativeNode.parentElement);

      if (element) {
        return getId(element);
      }
    }
  }

  return null;
};

const isSelectionLeftToRight = (sel: Selection) => {
  const doc = getDocument(sel.anchorNode);
  let leftToRight = true;

  if (doc && !sel.isCollapsed && sel.focusNode && sel.anchorNode) {
    const range = doc.createRange();
    range.setStart(sel.focusNode, sel.focusOffset);
    range.setEnd(sel.anchorNode, sel.anchorOffset);
    leftToRight = range.collapsed;
  }

  return leftToRight;
};

const getRangeClientRect = (_range: Range): ClientRect | null => {
  let rangeClientRect: DOMRect | undefined = _range.getBoundingClientRect();

  // Safari workaround: when calling getBoundingClientRect on range in Safari,
  // we end up with a 0 top/height/bottom/right rect that positions the range
  // in the wrong place. This method ensures a valid rect using getClientRects
  // as a fallback to get the range's ClientRect objects alternatively
  if (rangeClientRect.height === 0) {
    rangeClientRect = _range.getClientRects()[0];
  }

  return rangeClientRect || null;
};

type SelectionOrRange =
  | {
      type: 'selection';
      selection: Selection;
    }
  | {
      type: 'range';
      range: Range;
    };

export const selectionPropsFromSelectionOrRange = (
  nativeSubtree: HTMLElement,
  selectionOrRange: SelectionOrRange,
  adapter: ContentEditorViewportAdapter
): RangeSelectionWithNodesType => {
  if (selectionOrRange.type === 'selection') {
    const sel = selectionOrRange.selection;
    const _range = sel.getRangeAt(0);
    const _rangeClientRect = getRangeClientRect(_range);
    const _transformedRangeClientRect =
      _rangeClientRect &&
      adapter.transformRangeClientRect(_rangeClientRect, {
        nativeNode: nativeSubtree,
      });

    return {
      nativeAnchorNode: sel.anchorNode,
      nativeFocusNode: sel.focusNode,
      anchorOffset: sel.anchorOffset,
      focusOffset: sel.focusOffset,
      leftToRight: isSelectionLeftToRight(sel),
      // @ts-expect-error RangeSelectionWithNodesType expects this to be a Box, but getRangeClientRect says null is sometimes okay
      rangeClientRect: _transformedRangeClientRect,
    };
  } else if (selectionOrRange.type === 'range') {
    const range = selectionOrRange.range;
    const rangeClientRect = getRangeClientRect(range);
    const transformedRangeClientRect =
      rangeClientRect &&
      adapter.transformRangeClientRect(rangeClientRect, {
        nativeNode: nativeSubtree,
      });

    return {
      nativeAnchorNode: range.startContainer,
      nativeFocusNode: range.endContainer,
      anchorOffset: range.startOffset,
      focusOffset: range.endOffset,
      leftToRight: true,
      // @ts-expect-error RangeSelectionWithNodesType expects this to be a Box, but getRangeClientRect says null is sometimes okay
      rangeClientRect: transformedRangeClientRect,
    };
  } else {
    throw new Error('Requires a selection or range');
  }
};

const getSelection = (maybeSelection: Selection | null | undefined) => {
  if (maybeSelection) {
    return {
      type: 'selection',
      selection: maybeSelection,
    } as const;
  }
  return null;
};

const getRange = (maybeRange: Range | null | undefined) => {
  if (maybeRange) {
    return {
      type: 'range',
      range: maybeRange,
    } as const;
  }
  return null;
};

export const parseNativeSelection = (
  subtree: ExpressionElement,
  nativeSubtree: HTMLElement,
  {
    nativeSelection,
    range,
    nodeId,
  }: {
    nativeSelection?: Selection | null;
    range?: Range;
    nodeId?: string;
  },
  adapter: ContentEditorViewportAdapter
): ParsedSelectionType | null | undefined => {
  const selectionOrRange: SelectionOrRange | null =
    getSelection(nativeSelection) || getRange(range);

  if (nodeId) {
    const nativeFigureEl = getNativeNodeFromElement(
      subtree,
      nodeId,
      nativeSubtree
    );
    const layoutNode = getNativeNodeForToolbarBounds(
      subtree,
      nodeId,
      nativeFigureEl
    );

    const nativeRect = layoutNode && layoutNode.getBoundingClientRect();
    const nodeClientRect =
      nativeRect &&
      adapter.transformClientRect(nativeRect, {
        nativeNode: layoutNode,
      });

    return {
      type: 'Node',
      nodeId,
      nodeClientRect,
    };
  } else if (selectionOrRange) {
    const {
      nativeAnchorNode,
      anchorOffset,
      nativeFocusNode,
      focusOffset,
      leftToRight,
      rangeClientRect,
    } = selectionPropsFromSelectionOrRange(
      nativeSubtree,
      selectionOrRange,
      adapter
    );

    if (nativeAnchorNode && nativeFocusNode) {
      const anchorNodeId =
        getNodeIdFromNativeNode({subtree, nativeNode: nativeAnchorNode}) || '';
      const focusNodeId =
        getNodeIdFromNativeNode({subtree, nativeNode: nativeFocusNode}) || '';

      const selection = RangeSelectionRecord({
        anchorNodeId,
        focusNodeId,
        anchorOffset,
        focusOffset,
        leftToRight,
      });

      const selectedElements = getSelectedElements(subtree, selection);

      const selectedFigureEdgeCase = getSelectedFigureSafariFallback(
        subtree,
        selection
      );

      if (
        isEmbedSelected(selectedElements) ||
        isCodeBlockSelected(selectedElements) ||
        isFigureSelected(selectedElements) ||
        isUserComponentSelected(selectedElements) ||
        selectedFigureEdgeCase
      ) {
        const selectedElement = selectedFigureEdgeCase || selectedElements[0];
        if (!selectedElement) return;

        const selectedNodeId = selectedElement.id;

        const nativeNode = getNativeNodeFromElement(
          subtree,
          selectedNodeId,
          nativeSubtree
        );
        const layoutNode = getNativeNodeForToolbarBounds(
          subtree,
          selectedNodeId,
          nativeNode
        );

        const nativeRect = layoutNode && layoutNode.getBoundingClientRect();
        const nodeClientRect =
          nativeRect &&
          adapter.transformClientRect(nativeRect, {
            nativeNode: layoutNode,
          });

        return {
          type: 'Node',
          nodeId: selectedNodeId,
          nodeClientRect,
        };
      } else if (anchorNodeId && focusNodeId) {
        return {
          type: 'Range',
          anchorNodeId,
          focusNodeId,
          anchorOffset,
          focusOffset,
          leftToRight,
          rangeClientRect,
        };
      }
    }
  }

  // Get default selection
  return null;
};

export const getKeyboardShortcut = (
  state: ContentEditorActive,
  event: KeyboardEvent | React.KeyboardEvent
) => {
  const cmd = event.metaKey || event.ctrlKey;
  const key = event.which;
  const shift = event.shiftKey;

  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  const isWithinList =
    key === TAB && rangeSelection
      ? Boolean(
          getClosestListElement(EElement(subtree), rangeSelection.anchorNodeId)
        )
      : false;

  // Handle number field increment/decrement shortcuts
  // note that this overrides the default browser behavior of up and down keys.
  // It's important it's only for Number type so arrows continue to move the caret in other types.
  if (!cmd && isNumberEditing(state)) {
    if (shift && key === UP) {
      return SHORTCUTS.NUMBER_INCREMENT_BY_TEN;
    } else if (shift && key === DOWN) {
      return SHORTCUTS.NUMBER_DECREMENT_BY_TEN;
    } else if (key === UP) {
      return SHORTCUTS.NUMBER_INCREMENT;
    } else if (key === DOWN) {
      return SHORTCUTS.NUMBER_DECREMENT;
    }
  }

  if (cmd && key === B) {
    return SHORTCUTS.BOLD;
  } else if (cmd && key === I) {
    return SHORTCUTS.ITALIC;
  } else if (cmd && key === K) {
    return SHORTCUTS.LINK;
  } else if (cmd && key === X) {
    return SHORTCUTS.CUT;
  } else if (cmd && key === C) {
    return SHORTCUTS.COPY;
  } else if (cmd && key === V) {
    return SHORTCUTS.PASTE;
  } else if (shift && key === ENTER) {
    return SHORTCUTS.BR;
  } else if (shift && key === SPACE) {
    return SHORTCUTS.NBSP;
  } else if (cmd && shift && key === Z) {
    return SHORTCUTS.REDO;
  } else if (cmd && key === Z) {
    return SHORTCUTS.UNDO;
  } else if (shift && key === TAB && isWithinList) {
    return SHORTCUTS.OUTDENT;
  } else if (key === TAB && isWithinList) {
    return SHORTCUTS.INDENT;
  } else if (
    (cmd && key === S) ||
    key === ESC ||
    (key === TAB && !isWithinList)
  ) {
    return SHORTCUTS.STOP_EDITING;
  } else if (cmd && key === A) {
    return SHORTCUTS.SELECT_ALL;
  } else if (cmd && key === U) {
    return SHORTCUTS.UNDERLINE;
  }
};

export const isPlainTextPasteShortcut = (
  event: KeyboardEvent | React.KeyboardEvent
): boolean => {
  return event.ctrlKey && event.shiftKey && !event.metaKey && event.which === V;
};

/**
 * Read the current anchor node's text content directly from the DOM.
 *
 * During native typing (allowNativeRender === true), React does not
 * reconcile on every keystroke, so store-backed text (editState.subtree)
 * lags the DOM by N keystrokes. Callers in input/key event handlers
 * should prefer this helper over reading from store state when they need
 * to see the text the user is currently looking at.
 */
export const getNativeAnchorText = (
  win: Window | null | undefined
): string | undefined =>
  win?.getSelection()?.anchorNode?.textContent ?? undefined;

export const getTextShortcut = (
  state: ContentEditorActive,
  event: KeyboardEvent | React.KeyboardEvent
): string => {
  const {subtree, selection} = state;

  // Only allow text shortcuts for rich text elements
  if (!isRichText(subtree)) return '';

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return '';

  const key = event.which;

  const anchorNode = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );
  if (!anchorNode || !isStringElement(anchorNode)) return '';

  // If typing a space before a regular space or nbsp let it skip over the space
  if (
    key === SPACE &&
    isSpaceOrNbsp(getCharAfterCaret(subtree, rangeSelection))
  )
    return SHORTCUTS.RIGHT_ARROW;

  const typoNode = getClosestTopLevelTypoElement(
    rangeSelection.anchorNodeId,
    subtree
  );

  if (
    key === SPACE &&
    typoNode &&
    isParagraph(typoNode) &&
    (getChildren(typoNode) || []).length === 1
  ) {
    // Typing a space after '-' or '1.' creates a new list
    const textValue = Expressions.getText(anchorNode.data) || '';
    if (/^\d+\.$/.test(textValue)) {
      return SHORTCUTS.OL;
    } else if (textValue === '-') {
      return SHORTCUTS.UL;
    } else if (textValue === '>') {
      return SHORTCUTS.BLOCKQUOTE;
    } else if (/^\#{1,6}$/.test(textValue)) {
      return SHORTCUTS.HEADING;
    }
  }
  return '';
};

export const getMarkdownKey = (
  event: React.KeyboardEvent<HTMLInputElement>
): InlineMarkdownType | null => {
  const usedShiftKey = event.shiftKey;
  const key = event.which;

  switch (key) {
    case ASTERISK:
    case UNDERSCORE:
      return usedShiftKey ? 'bold italic' : null;
    case OPENING_PARENTHESES:
    case CLOSING_PARENTHESES:
      return usedShiftKey ? 'link' : null;
    case BACKTICK:
      return 'code';
    default:
      return null;
  }
};

// Looks at the current text node and returns if it finds markdown syntax.
// Currently restricted to only rich text blocks/fields
// Only returns true if the markdown key matches the markdown found
export const hasMatchingMarkdownInCurrentNode = ({
  markdownKey,
  state,
}: {
  markdownKey: InlineMarkdownType;
  state: ContentEditorActive;
}): boolean => {
  const {subtree, selection} = state;

  if (!isRichText(subtree)) return false;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return false;
  const anchor = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );
  if (!anchor) return false;
  const textValue = Expressions.getText(anchor.data);
  if (!textValue) return false;
  const markdownType = getInlineMarkdownTypeInText(textValue);
  if (!markdownType) return false;

  return markdownType === markdownKey;
};

export const applySelectionToWindow = (win: WindowProxy, range: Range) => {
  if (range) {
    // @ts-expect-error - TS2339 - Property 'select' does not exist on type 'Range'.
    if (typeof range.select !== 'undefined') {
      // @ts-expect-error - TS2339 - Property 'select' does not exist on type 'Range'.
      range.select();
    } else if (typeof win.getSelection !== 'undefined') {
      const sel = win.getSelection();
      // @ts-expect-error - TS18047 - 'sel' is possibly 'null'.
      sel.removeAllRanges();
      // @ts-expect-error - TS18047 - 'sel' is possibly 'null'.
      sel.addRange(range);
    }
  }
};

export const clearNativeSelection = (win: WindowProxy | null) => {
  const sel = win?.getSelection();
  sel?.removeAllRanges();
};

export const applySelectionFromRecord = (
  selectionRecord: SelectionRecordType,
  subtree: ExpressionElement,
  nativeSubtree: HTMLElement,
  editingNodeSettings?: boolean
) => {
  const win = getWindow(nativeSubtree);

  if (!win) return;

  // do not focus if the user is editing the expression editor menu, as it will
  // cause the menu to lose focus and close before a selection is made
  if (!editingNodeSettings) {
    preserveScrollAndFocus(nativeSubtree);
  }

  try {
    // For Node selections:
    // This will just stick the native caret at the beginning of the figureNode
    // We do this mostly so we can still listen for paste events

    // For Range selections:
    // This will just apply it to the native selection as expected.
    const range = rangeFromSelectionRecord(
      win.document,
      selectionRecord,
      subtree,
      nativeSubtree
    );
    applySelectionToWindow(win, range);
  } catch (error: any) {
    // Note: there are some edge cases where the subtree gets updated in the
    // store while a selection updates. For those cases we should investigate
    // and fix them, but rangeFromSelectionRecord's default behavior to throw
    // and trigger an `undo` is too severe for applying the selection here.

    // TODO: consider reporting bug here. In most cases is corrects itself when
    // the second thing racing completes, and other cases the user could just
    // click back in to fix it, but once the ContentEditor is more stable we
    // may want to go through these cases and figure out better fixes for them.
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'selection could not be applied, possible race condition',
        error
      );
    }
  }
};

// The native selection offsets will either be based on the childNodes or
// characters in the node value
// @ts-expect-error - TS2315 - Type 'NodeList' is not generic.
const getOffsetContext = (node: Node): string | NodeList<Node> =>
  isNativeTextNode(node) ? node.nodeValue : node.childNodes;

export const rangeFromSelectionRecord = (
  doc: Document,
  selectionRecord: SelectionRecordType,
  subtree: ExpressionElement,
  nativeSubtree: HTMLElement
) => {
  const range = doc.createRange();

  const nodeSelection = assertNodeSelectionRecordType(selectionRecord);

  if (nodeSelection?.nodeId) {
    const nativeFigure = getNativeNodeFromElement(
      subtree,
      nodeSelection.nodeId,
      nativeSubtree
    );

    if (nativeFigure) {
      range.setStart(nativeFigure, 0);
      range.setEnd(nativeFigure, 0);
      return range;
    }
  }

  const rangeSelection = assertRangeSelectionType(selectionRecord);

  const nativeAnchor =
    rangeSelection &&
    getNativeNodeFromElement(
      subtree,
      rangeSelection.anchorNodeId,
      nativeSubtree
    );
  const nativeFocus =
    rangeSelection &&
    getNativeNodeFromElement(
      subtree,
      rangeSelection.focusNodeId,
      nativeSubtree
    );

  if (nativeAnchor && nativeFocus && rangeSelection) {
    // Note: There are certain race conditions when typing super super fast
    // where the offset requested will be larger than the native textNode's
    // value (until it gets re-rendered later) due to the way events are
    // queued up vs rendering. If that happens it'll correct itself, so not to
    // worry, just cap it at the node's value length.
    const anchorOffset = Math.min(
      rangeSelection.anchorOffset,
      getOffsetContext(nativeAnchor).length
    );
    const focusOffset = Math.min(
      rangeSelection.focusOffset,
      getOffsetContext(nativeFocus).length
    );

    // Note: We probably still want to log it locally to help with debugging when it's caused by another bug
    if (
      process.env.NODE_ENV === 'development' &&
      (anchorOffset !== rangeSelection.anchorOffset ||
        focusOffset !== rangeSelection.focusOffset)
    ) {
      console.warn(
        // @ts-expect-error - TS18047 - 'nativeAnchor.nodeValue' is possibly 'null'. | TS18047 - 'nativeFocus.nodeValue' is possibly 'null'.
        `SelectionRecord offsets {anchor: ${selectionRecord.anchorOffset}, focus: ${selectionRecord.focusOffset}} were larger than than the native textNode's value length {anchor: ${nativeAnchor.nodeValue.length}, focus: ${nativeFocus.nodeValue.length}}`
      );
    }

    if (rangeSelection && rangeSelection.leftToRight) {
      range.setStart(nativeAnchor, anchorOffset);
      range.setEnd(nativeFocus, focusOffset);
    } else {
      range.setStart(nativeFocus, focusOffset);
      range.setEnd(nativeAnchor, anchorOffset);
    }

    return range;
  }

  throw new Error('Could not find anchor or focus native nodes on the page');
};

export const getImgNodeRects = (
  nativeSubtree: HTMLElement,
  activeUploads: Map<string, ActiveUploadRecordType>,
  adapter: ContentEditorViewportAdapter
): ImgNodeRectsType => {
  const imgNodeRects: Record<string, Box> = {};

  nativeSubtree.querySelectorAll('figure').forEach((figureEl) => {
    const imageFigureId = figureEl.getAttribute('data-w-id');

    // We only care about measuring figures with actively uploading images.
    if (!imageFigureId || !activeUploads.has(imageFigureId)) {
      return;
    }

    const imgEl = figureEl.querySelector('img');
    if (imageFigureId && imgEl) {
      const nodeClientRect = imgEl.getBoundingClientRect();
      const rect = adapter.transformClientRect(nodeClientRect, {
        nativeNode: imgEl,
      });

      // If the image width/height are 0 then we don't want to set the
      // measurement yet.. This will ensure it keeps trying to measure until
      // it's got some dimensions
      if (rect.width && rect.height) {
        imgNodeRects[imageFigureId] = rect;
      }
    }
  });

  return imgNodeRects;
};

export const initClickRange = (
  initEvent: ClickEventData,
  editTargetElement: ExpressionElement,
  editTargetNativeNode: HTMLElement,
  flux: Record<any, any>,
  adapter: ContentEditorViewportAdapter
) => {
  // We re-set the range from click event one frame later with the correct position

  // @ts-expect-error - TS18047 - 'initEvent.target' is possibly 'null'. | TS2339 - Property 'ownerDocument' does not exist on type 'EventTarget'.
  const targetDoc = initEvent.target.ownerDocument;
  const initialRange =
    targetDoc.caretRangeFromPoint &&
    targetDoc.caretRangeFromPoint(initEvent.clientX, initEvent.clientY);

  if (targetDoc.caretRangeFromPoint && !initialRange) {
    const win = getWindow();
    if (win?.requestAnimationFrame) {
      win.requestAnimationFrame(() => {
        // range will now have a value here.
        const parsedSelection = parseSelectionFromClickEvent(
          editTargetElement,
          initEvent,
          editTargetNativeNode,
          adapter
        );

        if (parsedSelection) {
          flux.dispatch(selectionUpdated({parsedSelection}));
        }
      });
    }
  }
};

export function preserveScrollAndFocus(nativeElement: HTMLElement) {
  const doc = getDocument(nativeElement);
  if (!doc) return;
  const docEl = doc.documentElement;
  // The data manager scroll container for editing items is not the document.
  // TODO (Yoav): we should probably add a better 'closest' search for advanced
  // edge cases in the designer/editor canvases where users have implemented
  // a custom scroll container
  const closestScrollPane = nativeElement.closest(
    '.kit-scroll-pane, .w-editor-scrollpane'
  );
  const scrollContainer = closestScrollPane || docEl;
  const scrollTop = scrollContainer.scrollTop;

  nativeElement.focus();
  scrollContainer.scrollTop = scrollTop;
}

const imageUploadCancelConfirmation =
  'Uploads in progress, Saving the Rich Text field now will terminate them. Click cancel to let uploads finish.';

export function safelyStopEditing(
  contentEditorState: ContentEditorActive,
  stopEditingText: any
): boolean {
  const {activeUploads} = contentEditorState;

  if (activeUploads.size === 0) {
    stopEditingText();
    return true;
  }

  if (window.confirm(imageUploadCancelConfirmation)) {
    stopEditingText();
    return true;
  }

  // Did not stop editing
  return false;
}

const isMultiLineDataType = (dataType: Type) => {
  const listType = Types.getListItemType(dataType);
  // If the element type is a List type
  if (listType && Types.isElementType(listType)) {
    const meta = getMeta(listType);
    return Boolean(meta && meta.multiLine);
  }

  const name = Types.getTypeConstructor(dataType);
  // If the element name is type 'Text'
  if (name && namesEqual(name, textTypeName)) {
    const meta = getMeta(dataType);
    return Boolean(meta && meta.multiLine);
  }

  return false;
};

const isEditingMultiLineTargetElement = ({
  targetNativeId,
  selectedNativeIdPath,
  focusedSymbolNativeIdPath,
  components,
  plugins,
}: {
  targetNativeId: NativeId;
  selectedNativeIdPath: NativeId[];
  focusedSymbolNativeIdPath: NativeId[];
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}) => {
  const currentComponent = getCurrentComponent(
    focusedSymbolNativeIdPath,
    components
  );
  const targetElementId = getGuidNullable(targetNativeId);
  const targetElement =
    currentComponent && targetElementId
      ? Expressions.getElementById(EElement(currentComponent), targetElementId)
      : null;
  if (!targetElement) return false;
  const fieldId = getSelectedFieldId({
    targetElement,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath,
    components,
    plugins,
  });
  const elementDataType =
    getDataTypeFromElement(plugins)(components)(targetElement);
  const recordFields = Types.getRecordFields(elementDataType);
  const selectedField = fieldId && recordFields ? recordFields[fieldId] : null;

  if (selectedField) {
    // For the purpose of editing component properties on the Canvas, we consider
    // Rich Text elements to be multi-line here
    return (
      Types.isRichTextChildrenType(selectedField) ||
      isMultiLineDataType(selectedField)
    );
  } else {
    return false;
  }
};

export const isEditingMultiLineNodeInInstance = (
  environmentContext: EnvironmentContextType,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType
) => {
  const selectedNativeIdPath = environmentContext.get('selectedNativeIdPath');
  const focusedSymbolNativeIdPath = environmentContext.get(
    'focusedSymbolNativeIdPath'
  );
  const editTargetNativeIdPath = environmentContext.get(
    'editTargetNativeIdPath'
  );

  return selectedNativeIdPath &&
    focusedSymbolNativeIdPath &&
    editTargetNativeIdPath
    ? isEditingMultiLineTargetElement({
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        targetNativeId: first(
          getRelativePath(editTargetNativeIdPath, focusedSymbolNativeIdPath)
        ),
        selectedNativeIdPath,
        focusedSymbolNativeIdPath,
        components,
        plugins,
      })
    : false;
};

const getSelectedFieldId = ({
  targetElement,
  selectedNativeIdPath,
  focusedSymbolNativeIdPath,
  components,
  plugins,
}: {
  targetElement: ExpressionElement;
  selectedNativeIdPath: NativeId[];
  focusedSymbolNativeIdPath: NativeId[];
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}) => {
  const updatedLinkedFieldsPathMap = targetElement
    ? getLinkedFieldsPathMapForElement(
        targetElement,
        selectedNativeIdPath,
        focusedSymbolNativeIdPath,
        components,
        plugins
      )
    : {};

  const innermostSymbolInstance = getInnermostSymbolInstance(
    selectedNativeIdPath,
    components
  );
  if (!innermostSymbolInstance || !targetElement) return null;

  const nodeInInstanceElement = getTargetElementOrInstance(
    selectedNativeIdPath,
    components
  );
  const nodeInInstanceElementRoot = getComponentInstRootElement(components)(
    innermostSymbolInstance
  );

  if (nodeInInstanceElement && nodeInInstanceElementRoot) {
    const editableTypographicTargetElement = getEditableTypographicElement(
      nodeInInstanceElement,
      nodeInInstanceElementRoot
    );
    const editableRTEElement = getEditableRTEElement(
      nodeInInstanceElement,
      nodeInInstanceElementRoot
    );

    const editTargetElement =
      editableRTEElement || editableTypographicTargetElement;

    if (editTargetElement) {
      return nodeInInstanceElementRoot
        ? getOutermostFieldIdFromPath(
            editTargetElement.id,
            nodeInInstanceElementRoot.id,
            'children',
            components,
            updatedLinkedFieldsPathMap
          )
        : null;
    }
  } else {
    return null;
  }
};

export const updateOverrideFieldValue = ({
  targetElementId,
  selectedNativeIdPath,
  focusedSymbolNativeIdPath,
  subtree,
  components,
  plugins,
  dispatch,
}: {
  targetElementId: NativeId;
  selectedNativeIdPath: NativeId[];
  focusedSymbolNativeIdPath: NativeId[];
  subtree: ExpressionElement;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  dispatch: (arg1?: any) => void;
}): void => {
  const currentComponent = getCurrentComponent(
    focusedSymbolNativeIdPath,
    components
  );
  const elementId = getGuidNullable(targetElementId);
  const targetElement =
    currentComponent && elementId
      ? Expressions.getElementById(EElement(currentComponent), elementId)
      : null;

  if (targetElement) {
    const updatedElements = Expressions.getRecord(subtree.data)?.children;
    if (!updatedElements) return;

    const fieldId = getSelectedFieldId({
      targetElement,
      selectedNativeIdPath,
      focusedSymbolNativeIdPath,
      components,
      plugins,
    });

    if (fieldId) {
      const targetComponent = components.get(targetElement.type);
      const updatedFields = maybeApplyNumberFormatting(
        targetComponent,
        fieldId,
        updatedElements
      );

      if (updatedFields !== null) {
        const coercedFields = maybeApplyPlainTextConversion(
          targetComponent,
          fieldId,
          updatedFields
        );
        if (coercedFields !== null) {
          dispatchExpressionAndDataTypeChange(
            targetElement,
            coercedFields,
            fieldId,
            dispatch
          );
        }
      }
    }
  }
  dispatch(textOfNodeSelectedInInstanceEditingEnded());
};

// gets number value by fieldId and applies number constraints. Returns null if NaN.
export const maybeApplyNumberFormatting = (
  targetComponent: Component.ComponentType | undefined,
  fieldId: string,
  expressionValue: Expression
): Expression | null => {
  if (!targetComponent) {
    return expressionValue;
  }
  const targetComponentDataType = Component.getDataType(targetComponent);
  const recordFields = Types.getRecordFields(targetComponentDataType);
  const targetField = recordFields ? recordFields[fieldId] : null;
  if (!targetField || !Types.equal(targetField, TNumber)) {
    return expressionValue;
  }
  const value = Expressions.getList(expressionValue);
  const firstValue = value && value[0];
  const valueEl = firstValue ? Expressions.getElement(firstValue) : null;
  if (valueEl && valueEl.data) {
    const meta = getMeta(targetField)?.value;

    const constraints: NumberConstraintsType = {
      minValue: meta?.min,
      maxValue: meta?.max,
      format: meta?.precision === 0 ? 'integer' : 'decimal',
      precision: meta?.precision,
    };
    const numberStr = Expressions.getText(valueEl.data);
    const parsedNumber = numberStr
      ? parseNumberWithConstraints(numberStr, constraints)
      : null;

    return parsedNumber !== null ? ENumber(parsedNumber) : null;
  }
  return expressionValue;
};

export const maybeApplyPlainTextConversion = (
  targetComponent: Component.ComponentType | undefined,
  fieldId: string,
  expressionValue: Expression
): Expression | null => {
  if (!targetComponent) {
    return expressionValue;
  }
  const targetComponentDataType = Component.getDataType(targetComponent);
  const recordFields = Types.getRecordFields(targetComponentDataType);
  const targetField = recordFields ? recordFields[fieldId] : null;
  if (!targetField || !Types.equal(targetField, TText)) {
    return expressionValue;
  }
  if (Expressions.isText(expressionValue)) {
    return expressionValue;
  }

  const value = Expressions.getList(expressionValue);
  if (!value) {
    return null;
  }
  if (value.length === 0) {
    return EText('');
  }

  let text = '';
  let convertedAny = false;
  for (const item of value) {
    const valueEl = Expressions.getElement(item);
    if (valueEl) {
      text += getAllTextInElement(valueEl);
      convertedAny = true;
      continue;
    }
    if (Expressions.isText(item)) {
      text += Expressions.getText(item) ?? '';
      convertedAny = true;
    }
  }

  return convertedAny ? EText(text) : null;
};

export const dispatchExpressionAndDataTypeChange = (
  element: ExpressionElement,
  updatedElements: Expression,
  fieldId: string,
  dispatch: Dispatch
) => {
  return dispatch(
    dataTypeChanged({
      id: element.id,
      initialId: generateGuid(),
      action: {
        key: [{in: 'Record', at: fieldId}],
        type: 'EXPRESSION_ACTION',
        payload: {
          newExpression: updatedElements,
          meta: {},
        },
      },
    })
  );
};

// this is used for actually saving an empty value. Whereas returning null alone would mean no new value.
const EMPTY_VALUE = {newFieldValue: null};

// For CMS-bound text fields, figure out if the value was changed, and if so, get the new value to save
export const getUpdatedItemFieldValue = ({
  originalFieldValue,
  seedId,
  state,
  plugins,
  components,
  fieldType,
  fieldValidations,
}: {
  originalFieldValue: string | Array<JSONNode> | number;
  seedId: string;
  state: ContentEditorActive;
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
  fieldType?: string;
  fieldValidations?: {
    precision?: number;
    minValue?: number;
    maxValue?: number;
    format?: string;
    allowNegative?: boolean;
  };
}):
  | {newFieldValue: string | Array<JSONNode> | number}
  | typeof EMPTY_VALUE
  | null => {
  if (isRichText(state.subtree)) {
    // For Rich Text fields, we need to compare and save the subtree as JSON Nodes
    const [updatedSubtree] = cleanSubtreeForSave(state, seedId);
    if (!updatedSubtree) {
      return null;
    }

    const updatedSubtreeAsJsonNodes = new Tree(components)
      .getElementAsJSONNodesCompressed(updatedSubtree, plugins)
      .slice(1);

    if (
      Array.isArray(originalFieldValue) &&
      // If the field value length has changed, we know the value has changed.
      // We only need to do deep equality comparison when lengths are the same.
      originalFieldValue.length === updatedSubtreeAsJsonNodes.length
    ) {
      const normalizedOriginalFieldValue = normalizeRichTextNodes(
        originalFieldValue,
        plugins
      );

      // ensure we compare similarly normalized json nodes
      const normalizedUpdatedFieldValue = removeEmptyDataObjects(
        updatedSubtreeAsJsonNodes
      );

      // If the value hasn't changed, we don't need to make a save
      if (isEqual(normalizedOriginalFieldValue, normalizedUpdatedFieldValue)) {
        return null;
      }
    }

    if (!updatedSubtreeAsJsonNodes.length) {
      // If there's no rich text content, save an empty value
      return EMPTY_VALUE;
    }

    return {newFieldValue: updatedSubtreeAsJsonNodes};
  } else {
    // For Plain Text fields, we get and compare string values
    const newTextValue = getAllTextInElement(state.subtree);

    if (fieldType === 'Number') {
      if (newTextValue === '') {
        return EMPTY_VALUE;
      }

      // Use parseAndBindNumber to handle comma decimal separators and apply constraints
      const constraints = {
        precision: fieldValidations?.precision,
        minValue: fieldValidations?.minValue,
        maxValue: fieldValidations?.maxValue,
        allowNegative: fieldValidations?.allowNegative,
        format:
          fieldValidations?.format === 'integer'
            ? ('integer' as const)
            : ('decimal' as const),
      };

      const parsedValue = parseNumberWithConstraints(newTextValue, constraints);
      if (parsedValue === null) {
        return null;
      }
      return {newFieldValue: parsedValue};
    }

    // If the value hasn't changed, we don't need to make a save
    if (originalFieldValue === newTextValue) {
      return null;
    }

    return {newFieldValue: newTextValue};
  }
};

/**
 * When the browser moves the caret past a node-selectable element (code block,
 * embed, figure, user component) — e.g. because Shadow DOM prevents a valid
 * caret position — this corrects the selection to land on that element instead.
 *
 * Returns the corrected parsedSelection, or the original if no correction was
 * needed.
 */
export const getSelectionCorrectedForSkippedNode = (
  parsedSelection: ParsedSelectionType,
  editState: ContentEditorActive,
  nativeSubtree: HTMLElement,
  key: number,
  adapter: ContentEditorViewportAdapter
): ParsedSelectionType => {
  if (!isArrow(key)) return parsedSelection;

  const prevRange = assertRangeSelectionRecordType(editState.selection);
  if (!prevRange) return parsedSelection;

  const direction: 'left' | 'right' =
    key === RIGHT || key === DOWN ? 'right' : 'left';
  const targetElement = getTargetElement(direction)(editState);

  if (!targetElement || !isHiddenFromContentEditable(targetElement)) {
    return parsedSelection;
  }

  const prevTopLevel = getClosestTopLevelElement(
    prevRange.anchorNodeId,
    editState.subtree
  );
  const newAnchorNodeId =
    parsedSelection.type === 'Range' ? parsedSelection.anchorNodeId : null;
  const newTopLevel = newAnchorNodeId
    ? getClosestTopLevelElement(newAnchorNodeId, editState.subtree)
    : null;

  if (!prevTopLevel) {
    return parsedSelection;
  }

  const topLevelChanged = prevTopLevel.id !== newTopLevel?.id;

  // At this point we know there is a targetElement that is hidden, however if
  // that element is placed at a boundary there's nothing beyond it to land on.
  // If caret can't change / is blocked we know selection needs to be corrected.
  const nativeSelectionBlocked = !hasCaretMoved(
    editState.selection,
    parsedSelection
  );

  if (!topLevelChanged && !nativeSelectionBlocked) {
    return parsedSelection;
  }

  const correctedSelection = parseNativeSelection(
    editState.subtree,
    nativeSubtree,
    {nodeId: targetElement.id},
    adapter
  );

  if (!correctedSelection) return parsedSelection;

  applySelectionFromRecord(
    reifySelectionRecord(correctedSelection),
    editState.subtree,
    nativeSubtree
  );

  return correctedSelection;
};
